import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "microeconomics-sem-1-unit-9-market-failure-externalities-public-goods-notes",
  title: "Introductory Microeconomics Unit 9: Market Failure, Externalities, Public Goods & Coase Theorem",
  description: "Complete DU Sem 1 Unit 9 notes: Negative/positive externalities, Pigouvian taxes, Coase theorem, pure public goods, Samuelson condition & tragedy of the commons.",
  pubDate: "2025-08-22",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["Market Failure", "Externalities", "Public Goods", "Coase Theorem", "Pigouvian Tax", "DU Economics"],
  readMinutes: 17,
  heroEyebrow: "Sem 1 · Unit 9 Notes"
};

export function Body() {
  return (
    <>
      <p>
        The study of introductory microeconomics builds toward a single, powerful conclusion: competitive markets allocate resources efficiently. This is known as the First Fundamental Theorem of Welfare Economics. But what happens when markets fail to deliver this efficiency? Unit 9 covers the core concepts of market failure, specifically externalities, public goods, common resources, and asymmetric information. This 3,000+ word study guide covers everything you need to know for Delhi University's Semester 1 Microeconomics exam, including detailed theoretical explanations, 8+ fully solved numerical problems, and DU PYQ analysis.
      </p>

      <h2>1. Economic Efficiency &amp; Market Failure</h2>

      <h3>1.1 Pareto Efficiency Criteria</h3>
      <p>
        An allocation of resources is Pareto efficient (or Pareto optimal) if it is impossible to make any one individual better off without making at least one other individual worse off. For an economy to achieve overall Pareto efficiency, three simultaneous conditions must be met:
      </p>
      <ul>
        <li>
          <strong>Efficiency in Exchange (Consumption Efficiency):</strong> The Marginal Rate of Substitution (MRS) between any two goods must be the same for all consumers. If consumer A's MRSxy is 2 and B's is 1, A values good X more than B does. They can trade and both be better off. Efficiency requires MRSxy_A = MRSxy_B.
        </li>
        <li>
          <strong>Efficiency in Production:</strong> The Marginal Rate of Technical Substitution (MRTS) between any two inputs (like Labor and Capital) must be the same for all firms producing any goods. MRTSlk_X = MRTSlk_Y. This ensures that it's impossible to reallocate inputs to produce more of one good without producing less of another.
        </li>
        <li>
          <strong>Efficiency in Product Mix:</strong> The Marginal Rate of Transformation (MRT), which is the slope of the Production Possibility Frontier (PPF), must equal the consumers' Marginal Rate of Substitution (MRS). MRTxy = MRSxy. This ensures the economy produces the exact mix of goods consumers want.
        </li>
      </ul>

      <h3>1.2 The Welfare Theorems</h3>
      <p>
        <strong>First Fundamental Theorem of Welfare Economics:</strong> Under certain conditions (perfect competition, no externalities, no public goods, perfect information), every competitive market equilibrium is Pareto efficient. The invisible hand works perfectly.
      </p>
      <p>
        <strong>Second Fundamental Theorem of Welfare Economics:</strong> Any Pareto efficient allocation can be achieved as a competitive market equilibrium by appropriately redistributing initial endowments (lump-sum wealth transfers) and letting the market work. This implies that equity and efficiency can be separated.
      </p>

      <h3>1.3 Sources of Market Failure</h3>
      <p>
        Market failure occurs when the free market equilibrium is not Pareto efficient. The four main sources of market failure are:
      </p>
      <ol>
        <li><strong>Externalities:</strong> Uncompensated impacts of one person's actions on the well-being of a bystander.</li>
        <li><strong>Public Goods:</strong> Goods that are non-rival and non-excludable, leading to free-riding.</li>
        <li><strong>Market Power:</strong> Monopolies and oligopolies restrict output below the competitive level to raise prices.</li>
        <li><strong>Asymmetric Information:</strong> When one party in a transaction has more or better information than the other (Adverse Selection and Moral Hazard).</li>
      </ol>

      <h2>2. Externalities in Detail</h2>
      <p>
        An externality arises when a person engages in an activity that influences the well-being of a bystander and yet neither pays nor receives any compensation for that effect. 
        <ul>
          <li><strong>Marginal Private Cost (MPC):</strong> The cost borne directly by the producer.</li>
          <li><strong>Marginal External Cost (MEC):</strong> The uncompensated cost imposed on bystanders (e.g., pollution).</li>
          <li><strong>Marginal Social Cost (MSC):</strong> The total cost to society. MSC = MPC + MEC.</li>
          <li><strong>Marginal Private Benefit (MPB):</strong> The benefit received directly by the consumer.</li>
          <li><strong>Marginal External Benefit (MEB):</strong> The uncompensated benefit to bystanders (e.g., herd immunity from vaccines).</li>
          <li><strong>Marginal Social Benefit (MSB):</strong> The total benefit to society. MSB = MPB + MEB.</li>
        </ul>
      </p>

      <h3>2.1 Negative Production Externalities</h3>
      <p>
        Example: A steel plant polluting a river. The pollution imposes costs on fishermen downstream.
        Here, MSC &gt; MPC. The social cost of producing steel includes the private costs of the producers plus the costs to those affected adversely by the pollution.
        The market only considers MPC and MPB (Demand). Equilibrium is where MPC = MPB (or MPC = Price).
        The socially optimal level is where MSC = MSB.
        Because MSC &gt; MPC, the market quantity (Qm) is greater than the socially optimal quantity (Q*). The market overproduces goods with negative externalities. This overproduction creates a deadweight loss (DWL) because, for units between Q* and Qm, the social cost exceeds the social benefit.
      </p>

      <h3>2.2 Positive Production Externalities</h3>
      <p>
        Example: A technology company's R&amp;D creates knowledge spillovers. 
        Here, MSC &lt; MPC. The market underproduces the good relative to the socially optimal level.
      </p>

      <h3>2.3 Negative Consumption Externalities</h3>
      <p>
        Example: Smoking in a crowded room.
        Here, MSB &lt; MPB. The social benefit is less than the private benefit because of the secondhand smoke. The market overconsumes the good.
      </p>

      <h3>2.4 Positive Consumption Externalities</h3>
      <p>
        Example: Education or vaccination.
        Here, MSB &gt; MPB (MSB = MPB + MEB). The market underconsumes the good because individuals do not consider the benefit they provide to society.
      </p>

      <h2>3. Solutions to Externalities</h2>

      <h3>3.1 Private Solutions: The Coase Theorem</h3>
      <p>
        The Coase Theorem states that if private parties can bargain without cost over the allocation of resources, they can solve the problem of externalities on their own, regardless of the initial distribution of property rights.
      </p>
      <p><strong>Assumptions of Coase Theorem:</strong></p>
      <ul>
        <li>Well-defined, transferable property rights.</li>
        <li>Zero (or very low) transaction costs (costs of drafting contracts, finding parties, negotiating).</li>
        <li>Perfect information.</li>
      </ul>
      <p>
        <strong>How it works:</strong> If a factory's pollution causes Rs. 1000 of damage to a laundry, and the factory can install a scrubber for Rs. 500. 
        If the laundry has the right to clean air, the factory will pay Rs. 500 for the scrubber (cheaper than paying Rs. 1000 damage).
        If the factory has the right to pollute, the laundry will offer the factory Rs. 500 to Rs. 1000 to install the scrubber. In both cases, the efficient outcome (scrubber installed) is achieved.
      </p>
      <p><strong>Limits of Coase Theorem:</strong> In reality, transaction costs are rarely zero. When many parties are involved (e.g., global warming affecting millions), negotiation is impossible. Holdout problems and strategic behavior also cause bargaining to fail.</p>

      <h3>3.2 Public Policy Solutions</h3>
      <p>When private bargaining fails, governments can intervene:</p>
      <ul>
        <li>
          <strong>Pigouvian Corrective Taxes:</strong> A tax enacted to correct the effects of a negative externality. The optimal Pigouvian tax (t) is set exactly equal to the Marginal External Cost (MEC) at the socially optimal quantity (Q*). Thus, MPC + tax = MSC, internalizing the externality. 
        </li>
        <li>
          <strong>Pigouvian Subsidies:</strong> A subsidy equal to the Marginal External Benefit (MEB) at Q* to encourage positive externalities (e.g., subsidizing education).
        </li>
        <li>
          <strong>Tradable Pollution Permits (Cap-and-Trade):</strong> The government sets a cap on total pollution and issues tradable permits. Firms that can reduce pollution cheaply will sell permits; firms with high abatement costs will buy them. This achieves the target pollution reduction at the lowest possible total cost to society.
        </li>
        <li>
          <strong>Direct Regulation (Command and Control):</strong> The government dictates a maximum level of pollution or requires specific technologies. This is usually inefficient because it doesn't equate the marginal cost of abatement across different firms.
        </li>
      </ul>

      <h2>4. Public Goods &amp; Common Resources Matrix</h2>
      <p>Goods can be classified by two characteristics:</p>
      <ul>
        <li><strong>Excludability:</strong> Can people be prevented from using the good? (Yes = Excludable)</li>
        <li><strong>Rivalry in Consumption:</strong> Does one person's use diminish another's use? (Yes = Rival)</li>
      </ul>
      <p>This creates a 2x2 matrix:</p>
      <table border={1} cellPadding={8} style={{ borderCollapse: "collapse", width: "100%", marginBottom: "2rem" }}>
        <thead>
          <tr>
            <th></th>
            <th>Rival in Consumption</th>
            <th>Non-Rival in Consumption</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Excludable</strong></td>
            <td><strong>Private Goods</strong> (Ice cream, clothing)</td>
            <td><strong>Club Goods / Natural Monopolies</strong> (Cable TV, toll roads)</td>
          </tr>
          <tr>
            <td><strong>Non-Excludable</strong></td>
            <td><strong>Common Resources</strong> (Fish in the ocean, grazing land)</td>
            <td><strong>Public Goods</strong> (National defense, streetlights)</td>
          </tr>
        </tbody>
      </table>

      <h2>5. Pure Public Goods &amp; The Free-Rider Problem</h2>
      <p>
        Pure Public Goods are both non-excludable and non-rival. 
        Because they are non-excludable, people have an incentive to be "free riders"—enjoying the benefit of the good without paying for it. Because of the free-rider problem, private markets will underprovide (or not provide at all) public goods.
      </p>

      <h3>5.1 The Samuelson Condition</h3>
      <p>
        How much of a public good should the government provide? For private goods, the market sums individual demand curves <em>horizontally</em> (adding quantities at each price). 
        For public goods, because they are non-rival, everyone consumes the exact same quantity. Therefore, we sum individual demand curves (marginal willingness to pay) <em>vertically</em> (adding prices/willingness to pay at each quantity).
      </p>
      <p>
        The socially optimal provision of a public good is given by the <strong>Samuelson Condition</strong>:
        <br/>
        Sum(MRS) = MRT
        <br/>
        Or: Sum of Marginal Benefits (MSB) = Marginal Cost (MC).
      </p>

      <h2>6. Common Resources &amp; The Tragedy of the Commons</h2>
      <p>
        Common resources are non-excludable but rival. Anyone can use them, but one person's use leaves less for others.
        This leads to the <strong>Tragedy of the Commons</strong>: the tendency for a common resource to be overused and depleted.
      </p>
      <p>
        <strong>Economic Mechanism:</strong> Individuals base their extraction decisions on the Average Product (AP) or Average Revenue, ignoring the negative externality they impose on others (which lowers the marginal product). The social optimum is where Marginal Product (MP) or Marginal Revenue = Marginal Cost. Because AP &gt; MP (when MP is falling), open access leads to overexploitation.
      </p>
      <p>
        <strong>Government Remedies:</strong> 
        1. Assign private property rights (privatization).
        2. Set quotas (e.g., fishing limits).
        3. Taxes (e.g., entrance fees to national parks).
      </p>

      <h2>7. Asymmetric Information (Briefly)</h2>
      <p>When one side of the market knows more than the other.</p>
      <ul>
        <li><strong>Adverse Selection (Hidden Information):</strong> Happens <em>before</em> a transaction. Example: The Market for Lemons (Akerlof). Because buyers can't tell good cars from bad, they only offer an average price. Owners of good cars exit the market, leaving only "lemons."</li>
        <li><strong>Moral Hazard (Hidden Action):</strong> Happens <em>after</em> a transaction. Example: A person with fire insurance acts less carefully because the insurer bears the cost of a fire.</li>
        <li><strong>Signaling:</strong> The informed party takes an observable action to reveal private info (e.g., a warranty, a college degree).</li>
        <li><strong>Screening:</strong> The uninformed party induces the informed party to reveal info (e.g., insurance deductibles).</li>
      </ul>

      <hr />

      <h2>8. Fully Solved Numerical Problems</h2>
      <p>These 8 numericals cover the core patterns seen in DU Semester 1 PYQs.</p>

      <h3>Problem 1: Market vs. Social Optimum with Negative Externality</h3>
      <p>
        <strong>Question:</strong> A competitive market has demand given by P = 100 - Q and private supply given by P = 10 + Q. The production of the good generates pollution, creating a Marginal External Cost (MEC) of MEC = 0.5Q. 
        Find: (a) Market equilibrium Q and P. (b) Socially optimal Q and P. (c) The deadweight loss (DWL) of the free market.
      </p>
      <p><strong>Solution:</strong></p>
      <p>
        (a) Market Equilibrium sets MPB (Demand) = MPC (Supply).<br/>
        100 - Q = 10 + Q<br/>
        2Q = 90 &rArr; Qm = 45<br/>
        Pm = 100 - 45 = 55.
      </p>
      <p>
        (b) Social Optimum sets MSB = MSC.<br/>
        MSB = MPB = 100 - Q (no positive externality).<br/>
        MSC = MPC + MEC = (10 + Q) + 0.5Q = 10 + 1.5Q.<br/>
        100 - Q = 10 + 1.5Q<br/>
        2.5Q = 90 &rArr; Q* = 36<br/>
        Socially optimal price (paid by consumers) = 100 - 36 = 64.
      </p>
      <p>
        (c) DWL = 0.5 * (Qm - Q*) * (MSC at Qm - MSB at Qm).<br/>
        At Qm = 45:<br/>
        MSC = 10 + 1.5(45) = 10 + 67.5 = 77.5<br/>
        MSB = 100 - 45 = 55<br/>
        Difference = 77.5 - 55 = 22.5 (This is exactly MEC at Qm = 0.5 * 45 = 22.5).<br/>
        DWL = 0.5 * (45 - 36) * 22.5 = 0.5 * 9 * 22.5 = 101.25.
      </p>

      <h3>Problem 2: Calculating Pigouvian Tax</h3>
      <p>
        <strong>Question:</strong> Using the data from Problem 1, calculate the exact Pigouvian tax per unit required to achieve the social optimum.
      </p>
      <p><strong>Solution:</strong></p>
      <p>
        The optimal Pigouvian tax (t) must be equal to the Marginal External Cost (MEC) evaluated <em>at the socially optimal quantity (Q*)</em>.<br/>
        We found Q* = 36.<br/>
        MEC = 0.5Q<br/>
        t = MEC(36) = 0.5 * 36 = 18.<br/>
        <em>Check:</em> If a tax of 18 is imposed on producers, their new supply curve is MPC + t = (10 + Q) + 18 = 28 + Q. <br/>
        New Market Eq: 100 - Q = 28 + Q &rArr; 2Q = 72 &rArr; Q = 36. This perfectly matches Q*.
      </p>

      <h3>Problem 3: Positive Consumption Externality</h3>
      <p>
        <strong>Question:</strong> The market for flu vaccines has demand MPB = 50 - 2Q and supply MPC = 10 + 2Q. Each vaccine provides a Marginal External Benefit (MEB) of 8. Find the market and optimal quantities, and the optimal Pigouvian subsidy.
      </p>
      <p><strong>Solution:</strong></p>
      <p>
        Market Eq (MPB = MPC): 50 - 2Q = 10 + 2Q &rArr; 4Q = 40 &rArr; Qm = 10.<br/>
        Social Optimum (MSB = MSC). <br/>
        MSB = MPB + MEB = (50 - 2Q) + 8 = 58 - 2Q.<br/>
        MSC = MPC = 10 + 2Q.<br/>
        58 - 2Q = 10 + 2Q &rArr; 4Q = 48 &rArr; Q* = 12.<br/>
        Optimal Subsidy = MEB at Q* = 8. (Since MEB is constant, it's 8 everywhere).
      </p>

      <h3>Problem 4: Coase Theorem Negotiation</h3>
      <p>
        <strong>Question:</strong> Factory A profits 500 from polluting. Farm B suffers 800 in damages. A filter costs 300 to install. If B has the property rights to a clean environment, what is the outcome? If A has the right to pollute, what is the outcome? Assume zero transaction costs.
      </p>
      <p><strong>Solution:</strong></p>
      <p>
        <em>Case 1: B has property rights.</em> A must stop polluting unless it compensates B. B demands 800. A's profit is only 500, so A will not pay 800. Instead, A will pay 300 to install the filter. Total social cost is 300.<br/>
        <em>Case 2: A has property rights.</em> A will pollute. B suffers 800. B can offer to pay A to install the filter. B is willing to pay up to 800. The filter costs A 300. B pays A some amount between 300 and 800 to install the filter. <br/>
        <em>Conclusion:</em> In both cases, the filter is installed (the socially efficient outcome, since 300 &lt; 800 damage).
      </p>

      <h3>Problem 5: Public Goods &amp; Vertical Summation</h3>
      <p>
        <strong>Question:</strong> A town has two residents, Alice and Bob. Alice's demand for a public good (streetlights) is P1 = 30 - Q. Bob's demand is P2 = 20 - Q. The marginal cost of providing a streetlight is MC = 25. How many streetlights should be provided?
      </p>
      <p><strong>Solution:</strong></p>
      <p>
        We must sum the demand curves vertically. The total Marginal Social Benefit (MSB) is the sum of willingness to pay.<br/>
        MSB = P1 + P2 = (30 - Q) + (20 - Q) = 50 - 2Q.<br/>
        <em>Wait! We must be careful. Bob's willingness to pay cannot be negative.</em> Bob's demand hits zero at Q=20. So, for Q &gt; 20, Bob pays 0, and MSB is just Alice's demand.<br/>
        MSB = 50 - 2Q for 0 &lt;= Q &lt;= 20.<br/>
        MSB = 30 - Q for Q &gt; 20.<br/>
        Set MSB = MC to find the optimum.<br/>
        Assume Q is in the first range (Q &lt;= 20):<br/>
        50 - 2Q = 25 &rArr; 2Q = 25 &rArr; Q* = 12.5.<br/>
        Since 12.5 &lt;= 20, this is valid. The socially optimal number of streetlights is 12.5.
      </p>

      <h3>Problem 6: Tradable Pollution Permits</h3>
      <p>
        <strong>Question:</strong> Firm 1 has Marginal Abatement Cost MAC1 = 10 + 2q1. Firm 2 has MAC2 = 20 + q2. The government wants to reduce total emissions by 30 units (so total abatement q1 + q2 = 30). What is the cost-minimizing distribution of abatement?
      </p>
      <p><strong>Solution:</strong></p>
      <p>
        Cost minimization requires that marginal abatement costs are equalized across firms: MAC1 = MAC2.<br/>
        10 + 2q1 = 20 + q2<br/>
        We also know total abatement: q1 + q2 = 30 &rArr; q2 = 30 - q1.<br/>
        Substitute q2:<br/>
        10 + 2q1 = 20 + (30 - q1)<br/>
        10 + 2q1 = 50 - q1<br/>
        3q1 = 40 &rArr; q1 = 13.33.<br/>
        q2 = 30 - 13.33 = 16.67.<br/>
        By allowing permits to trade, the price of a permit will equalize at MAC1 = 10 + 2(13.33) = 36.67.
      </p>

      <h3>Problem 7: Tragedy of the Commons</h3>
      <p>
        <strong>Question:</strong> A lake has a total catch function of T = 100B - 2B^2, where B is the number of boats. The cost of running a boat is 40. Find the number of boats under open access and the socially optimal number of boats.
      </p>
      <p><strong>Solution:</strong></p>
      <p>
        Revenue/Catch is T. Average Revenue (AR) per boat = T/B = 100 - 2B.<br/>
        Marginal Revenue (MR) = dT/dB = 100 - 4B.<br/>
        Marginal Cost (MC) per boat = 40.<br/>
        <strong>Open Access (Market):</strong> Entry continues until profit per boat is zero, i.e., AR = MC.<br/>
        100 - 2B = 40 &rArr; 2B = 60 &rArr; Bo = 30 boats.<br/>
        <strong>Social Optimum:</strong> Maximize total profit. Set MR = MC.<br/>
        100 - 4B = 40 &rArr; 4B = 60 &rArr; B* = 15 boats.<br/>
        Under open access, there are twice as many boats as is socially optimal.
      </p>

      <h3>Problem 8: Market for Lemons (Adverse Selection)</h3>
      <p>
        <strong>Question:</strong> Buyers value High-quality cars at 10,000 and Low-quality cars at 5,000. Sellers value High at 8,000 and Low at 4,000. 50% of cars are High, 50% are Low. Buyers cannot observe quality beforehand, so they offer the expected value. Will High-quality cars be sold?
      </p>
      <p><strong>Solution:</strong></p>
      <p>
        Expected value to buyer = 0.5(10,000) + 0.5(5,000) = 7,500.<br/>
        Buyers will offer a maximum of 7,500.<br/>
        Sellers of High-quality cars value their cars at 8,000. Since 7,500 &lt; 8,000, they will not sell.<br/>
        Only Low-quality car sellers (valuing at 4,000) accept 7,500.<br/>
        When buyers realize only Low-quality cars are offered, their expected value drops to 5,000. The market completely fails for High-quality cars (Adverse Selection).
      </p>

      <hr />

      <h2>9. DU Exam Preparation &amp; PYQ Strategy</h2>

      <h3>9.1 High-Scoring Essay Structures</h3>
      <p>For theoretical 10-15 marker questions on externalities or public goods in DU exams, use this structure:</p>
      <ul>
        <li><strong>Introduction (1 mark):</strong> Define the core concept clearly (e.g., "A negative production externality occurs when..."). State whether MSB &gt; MPB or MSC &gt; MPC.</li>
        <li><strong>Graph (3-4 marks):</strong> Draw a large, well-labeled graph. Label axes (P, Q), label all curves (MPC, MSC, MPB), mark the market equilibrium (Qm, Pm) and social optimum (Q*, P*). Shade the Deadweight Loss (DWL) triangle cleanly.</li>
        <li><strong>Mechanism (3 marks):</strong> Explain the graph step-by-step. "Because producers only consider MPC, they produce at Qm. However, the social cost includes MEC. Between Q* and Qm, MSC &gt; MSB, generating a welfare loss."</li>
        <li><strong>Policy Solution (2-3 marks):</strong> Explain how a Pigouvian tax or subsidy fixes the problem. Mention that t = MEC at Q*.</li>
        <li><strong>Conclusion (1-2 marks):</strong> Summarize the welfare theorem failure.</li>
      </ul>

      <h3>9.2 PYQ Frequency Analysis</h3>
      <p>Based on the last 5 years of DU Introductory Microeconomics papers:</p>
      <ul>
        <li><strong>Highest Frequency:</strong> Public goods vertical summation numericals (like Problem 5). They appear almost every year. Practice handling the kinked demand curve.</li>
        <li><strong>High Frequency:</strong> Explaining Coase Theorem and its limitations. Remember to state the three core assumptions clearly.</li>
        <li><strong>Medium Frequency:</strong> Calculating the exact Pigouvian tax (like Problem 2). Make sure you calculate MEC at <em>Q*</em>, not at Qm.</li>
        <li><strong>Bonus Marks:</strong> If a question asks about emissions standards vs. taxes, always draw the Marginal Abatement Cost (MAC) graphs to show that taxes equalize MAC across firms, whereas standards do not.</li>
      </ul>
      
      <p>
        Unit 9 is the bridge between theoretical efficiency and real-world policy. By mastering these graphs and numerical techniques, you don't just secure marks; you build the foundation for Public Economics and Environmental Economics in later semesters.
      </p>
    </>
  );
}


